import { useCognitoAuth } from '@/contexts/cognitoAuthContext'
import { DashboardExperiments } from '@/experiments'
import { useFlag } from '@/hooks/useFlag'
import { useGetAccountInfo } from '@/queries/account/useGetAccountInfo'
import { useGetSelfServiceInfo } from '@/queries/premium/useGetSelfServiceInfo'
import { CoreLevelFeatureNames } from '@/services/types/permissions/permissionFeatures/mockCorePermissionFeature'
import { useCorePermissions } from '@/stores/permissions/permissions'
import { getCurrentProvisionStatus } from '@/utils/functions/selfService/getCurrentProvisionSeatsStatus'
import { useMemo } from 'react'

export const useSelfServeAndPermissions = () => {
  const { accountId, isAuthenticated } = useCognitoAuth()

  const { data: accountInfo, isLoading: isAccountInfoLoading } = useGetAccountInfo({ accountId })
  const isSelfServeAccount = accountInfo?.accountType === 'SELF_SERVE'

  const { data: selfServiceInfo, isLoading: isSelfServiceInfoLoading } = useGetSelfServiceInfo({
    enabled: isAuthenticated,
  })

  const billingAndPaymentsPermission = useCorePermissions(CoreLevelFeatureNames.billingAndPayments)
  const upgradePackagePermission = useCorePermissions(CoreLevelFeatureNames.upgradePackage)

  const showBillingAndPaymentsPage = useFlag(DashboardExperiments.billingAndPaymentsPage)
  const showUpgradePackagePage = useFlag(DashboardExperiments.upgradePackagePage)

  const shouldShowBillingAndPayments = useMemo(() => {
    const hasPermission = !!billingAndPaymentsPermission?.view
    return showBillingAndPaymentsPage || (hasPermission && isSelfServeAccount)
  }, [billingAndPaymentsPermission?.view, showBillingAndPaymentsPage, isSelfServeAccount])

  const canDoUpgrade = useMemo(() => {
    const hasPermission = !!upgradePackagePermission?.view
    const hasUpgrade = !!selfServiceInfo?.hasUpgrade

    if (!hasUpgrade) return false

    return showUpgradePackagePage || (hasPermission && isSelfServeAccount)
  }, [
    upgradePackagePermission?.view,
    selfServiceInfo?.hasUpgrade,
    showUpgradePackagePage,
    isSelfServeAccount,
  ])

  const shouldDoUpgrade = useMemo(() => {
    return selfServiceInfo?.hasChosenFirstPackage === false
  }, [selfServiceInfo?.hasChosenFirstPackage])

  const shouldDoOnboarding = useMemo(() => {
    return selfServiceInfo?.storesAmount === 0
  }, [selfServiceInfo?.storesAmount])

  const seatsStatus = useMemo(() => {
    return getCurrentProvisionStatus(
      selfServiceInfo?.totalSeatsInUse || 0,
      selfServiceInfo?.totalSeatsAmount || 0,
    )
  }, [selfServiceInfo?.totalSeatsAmount, selfServiceInfo?.totalSeatsInUse])

  return {
    shouldShowBillingAndPayments,
    canDoUpgrade,
    isAccountInfoLoading,
    shouldDoUpgrade,
    isSelfServiceInfoLoading,
    shouldDoOnboarding,
    isSelfServeAccount,
    seatsStatus,
  }
}
